import { DataSecurityLevel, } from '@office-start/instrumentation-types';
import { addOnVisibilityChangeListener, getIsPageVisible, } from '@office-start/page-visibility-service';
import { cancel, performanceLoggingSupported, runAfterPaint, } from '@office-start/performance-utility';
import { logPerformanceError } from './logPerformanceError';
import { logPerformanceEvent } from './logPerformanceEvent';
import { getCurrentRouteIdentifier, getLastRecordedNavigationStartMarker, recordedMarkers, recordedMeasures, setCurrentRouteIdentifier, setLastRecordedNavigationStartMarker, } from './PerformanceServiceStates';
var queuedEvents = new Set();
var onHiddenListenerAdded = false;
var paintListenerHandle;
function logMeasure(name, originalName, securityThreshold, params) {
    var rawMeasure = window.performance.getEntriesByName(name, 'measure');
    if (!rawMeasure) {
        return;
    }
    var timingEntry = rawMeasure[0];
    var duration = Math.ceil(timingEntry.duration);
    // Use original marker name without route qualifier as entry name
    logPerformanceEvent(originalName, duration, params, securityThreshold);
}
function addMeasure(measureName, measureTo, measureFrom, securityThreshold, loggedName, params) {
    // Exit early if logging not requested for given mark
    if (!measureFrom) {
        return;
    }
    // Do not record duplicate measures.
    // IMPORTANT: Note that measure name includes route qualifier but does not include repeat counter.
    // This means that measure will be recorded only once and repeat markers will not have measures.
    // This is done intentionally since measures are logged and we only interested when something
    // happens for the first time. This eliminates boiler plate code that we would have to put in all
    // places where we call performanceService.mark() to record measure only when mark is recorded for
    // the first time.
    if (recordedMeasures.has(measureName)) {
        return;
    }
    // Preserve original name that will be used when dumping recorded measures for logging purposes
    recordedMeasures.add(measureName);
    // BUGBUG: The following call may throw exception in case startMark value is deemed to be invalid. This seem to
    // primarily happen on Edge/IE and Firefox. Wrapping into try/catch so we have direct way of checking how often it happens.
    // Consider removing try/catch in case this is no longer happening.
    try {
        window.performance.measure(measureName, measureFrom, measureTo);
    }
    catch (ex) {
        logPerformanceError(measureName, "Failed to record <" + measureName + "> for <" + measureTo + "> starting from <" + measureFrom + ">: exception is thrown");
        return;
    }
    // TODO: Do we need to queue marks to be measured later or can we just measure on demand?
    // If all Browser performance timing markers have been already dumped then log measure as soon as it comes
    // if (this.dumpedBrowserMarkers) {
    //     this.log(measureName);
    // } else {
    //     // Measure will be logged later together with browser markers; preserve global state values for such
    //     // measure so they are accurately reflected in logged data
    //     params = params || {};
    //     this.instrumentationService.applyGlobalStateParams(params, false);
    //     this.globalStateParamsForAppMeasures.set(measureName, params);
    // }
    logMeasure(measureName, loggedName || measureName, securityThreshold, params);
}
/**
 * Generates performance marker and generates measure with the same name which is calculated from given relative point.
 * The measure will be logged automatically unless PerformanceMeasureType.NoMeasure is specified. Marker and measure
 * names will be appended with current app route identifier and (for repeat marker) index corresponding for the number
 * of occurrences. On the other hand, event logged for calculated measure will have the canonical name passed to this
 * method. This is due to the fact that logged events carry additional information in separate Page property.
 * Also note that event will be logged only for first marker occurrence as subsequent markers are rarely interesting
 * from perf analysis perspective.
 * @param name Mark name. Before recording given name will be appended with current route and app name (if specified for the route).
 * For instance, if current route is 'Start' and app is 'Word' and mark name is 'Mru_Ready' then recorded name will be 'Mru_Ready_StartWord'.
 * If same mark name is produced multiple times then name will be automatically appended with index that corresponds to the count of
 * previous appearences of the same mark. For instance, when mark('Some_Mark') is called for first time then it will be recorded as
 * 'Some_Mark'; when mark('Some_Mark') is called for second time then it will be recorded as 'Some_Mark.2'; when mark('Some_Mark') is
 * called for third time then it will be recorded as 'Some_Mark.3' and so on.
 * @param measureFrom Point from which to measure duration that will be associated with the mark. By default it is set to
 * PerformanceMeasureType.FromLastAppNavigationStart which corresponds to the last navigation inside app. It can also be set to
 * PerformanceMeasureType.FromBrowserNavigationStart which corresponds to the time when user navigated to office.com in browser.
 * If measureFrom is string then it represents specific mark that was recorded beforehand.
 * Finally, PerformanceMeasureType.NoMeasure is used to specify that measure should not be recorded and logged.
 * @param securityThreshold Lowest security level at which this mark will be recorded. Omit to always allow.
 * @param params Optional parameters to include when measure is logged.
 * @returns Marker that was added to browser performance markers. Can be passed to future mark() calls as measureFrom argument.
 */
export function mark(name, measureFrom, securityThreshold, params) {
    if (measureFrom === void 0) { measureFrom = 2 /* FromLastAppNavigationStart */; }
    if (securityThreshold === void 0) { securityThreshold = DataSecurityLevel.Security; }
    if (!performanceLoggingSupported()) {
        return undefined;
    }
    // TODO: Do we need to set appsecuritylevel before marking or logging events?
    // This is here to prevent logging events that were previously queued before the security theshold changed.
    // This happens in a matter of seconds, so can we get away with assuming the setting hasn't changed between page load and logging initialization?
    // If we need to check this before queueing, then we'll need to initialize before calling mark() or any other instrumentation methods.
    // NOTE: We will not apply marks to browser performance object if the windows security settings do not allow it
    // To observe all marks ensure your Settings->Privacy->Diagnostics&Feedback is set to 'Enhanced' (MSIT doesn't allow 'Full')
    // if (securityThreshold > appSecurityLevel) {
    //     return undefined;
    // }
    // Append mark name with route identifier so it is easy to distinguish them if same mark is logged on different route
    var nameWithRouteQualifier = name;
    var currentRouteIdentifier = getCurrentRouteIdentifier();
    if (currentRouteIdentifier) {
        nameWithRouteQualifier = name + "_" + currentRouteIdentifier;
    }
    // Append count number if marker was already recorded before
    var marker = nameWithRouteQualifier;
    var markerCount = recordedMarkers.get(marker);
    if (markerCount) {
        var count = markerCount + 1;
        recordedMarkers.set(marker, count);
        marker = nameWithRouteQualifier + "." + count;
    }
    else {
        recordedMarkers.set(marker, 1);
    }
    window.performance.mark(marker);
    // Update last navigation start marker
    var lastNavigationStartMarker = getLastRecordedNavigationStartMarker() ||
        "navigationStart" /* NavigationStart */;
    if (name === "App_Navigation_Start" /* AppRouteNavigationStart */) {
        setLastRecordedNavigationStartMarker(marker);
    }
    // Calculate timing info from specified relative point
    var startMark = "navigationStart" /* NavigationStart */;
    if (measureFrom === 2 /* FromLastAppNavigationStart */) {
        startMark = lastNavigationStartMarker;
    }
    else if (measureFrom === 0 /* NoMeasure */) {
        startMark = '';
    }
    else if (typeof measureFrom === 'string') {
        startMark = measureFrom;
    }
    addMeasure(nameWithRouteQualifier, marker, startMark, securityThreshold, name, params);
    return marker;
}
export function markPaintEvent(paintMarkerName) {
    return new Promise(function (resolve) {
        runAfterPaint(function () {
            resolve(mark(paintMarkerName));
        });
    });
}
/**
 * Optional method to update the identifier appended to marker names and set a marker to which future marks will be compared against if
 * they elect to use the default measureFrom value PerformanceMeasureType.FromLastAppNavigationStart. The first call to this method will
 * generate a performance_dump event that measures the initial navigation latency against a supplied start point or response start.
 * @param routeIdentifier String appended to marker names.
 * @param measureInitialNavFrom Optional marker to measure the first navigation against. If not provided will measure against ResponseStart
 */
export function updatePerformanceRoute(routeIdentifier, measureInitialNavFrom) {
    setCurrentRouteIdentifier(routeIdentifier);
    // Log only first occurrence of appRouteNavigationStart marker; the rest occurrences will
    // happen at arbitrary point in time and less interesting from perf analysis perspective
    // We measure from responseStart in officehome or bootstrap event in pwa
    mark("App_Navigation_Start" /* AppRouteNavigationStart */, getLastRecordedNavigationStartMarker()
        ? 0 /* NoMeasure */
        : measureInitialNavFrom || "responseStart" /* ResponseStart */);
}
export function recordWhenPainted(markerName) {
    if (!onHiddenListenerAdded) {
        setupOnHiddenListener();
        onHiddenListenerAdded = true;
    }
    queuedEvents.add(markerName);
    if (paintListenerHandle) {
        return;
    }
    // Approximation of the browser's 'paint' of the frame.
    // See: https://microsoft.sharepoint.com/teams/edgeshare/SitePages/Perf-Club-Training-%E2%80%93-Advanced.aspx
    // => Accurately Measuring Layout
    paintListenerHandle = runAfterPaint(function () {
        if (getIsPageVisible()) {
            queuedEvents.forEach(function (marker) {
                mark(marker);
            });
        }
        resetListener();
    });
}
function setupOnHiddenListener() {
    addOnVisibilityChangeListener(function (state) {
        if (state === 'hidden') {
            cancelPaintListener();
        }
    });
}
function cancelPaintListener() {
    if (paintListenerHandle) {
        cancel(paintListenerHandle);
    }
    resetListener();
}
function resetListener() {
    queuedEvents.clear();
    paintListenerHandle = null;
}
//# sourceMappingURL=PerformanceService.js.map